import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Dropdown } from '@/components/shared/Dropdown';
import { AdminRole, adminRoles, Affiliation } from '@/types/affiliation';
import { getFullName } from '@/utils/name';
import { SharedButton } from '@/components/shared/Button';
import { Modal } from '@/components/shared/Modal';

import * as styles from './Style.module.scss';

type Props = {
  affiliation: Affiliation;
  onClickUpdateRole: (adminId: number, role: AdminRole) => void;
};

export const AssignRole: React.FC<Props> = ({
  affiliation,
  onClickUpdateRole,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [targetRole, setTargetRole] = useState<AdminRole>();

  const close = () => setIsOpen(false);
  const confirmAssign = (role: AdminRole) => {
    setIsOpen(true);
    setTargetRole(role);
  };

  const { admin, role: adminRole } = affiliation;
  const adminFullName = getFullName(admin.firstName, admin.lastName);
  const displayName = adminFullName || admin.email;

  return (
    <>
      <Dropdown
        dataTest="roles"
        buttonClassNames={styles.toggleButton}
        text={
          affiliation.role
            ? t(`models.affiliation.roleType.${adminRole}.name`)
            : ''
        }
        menuWrapperClassNames={styles.menuWrapper}
        menuNode={adminRoles.map((role) => (
          <div
            key={role}
            data-test={`${role}-role-${admin.email}`}
            className={classNames(
              styles.menuItem,
              affiliation.role === role ? 'active' : '',
            )}
            onClick={() => {
              if (affiliation.role !== role) {
                confirmAssign(role);
              }
            }}
          >
            {t(`models.affiliation.roleType.${role}.name`)}
          </div>
        ))}
      />

      <Modal
        isOpen={isOpen}
        onClose={close}
        title={t('ui.confirm.assignRole.title')}
        buttons={
          <>
            <SharedButton
              dataTest="cancel"
              onClick={close}
              color="gray"
              buttonType="tight"
              fontSize="small"
              fontWeight="bold"
            >
              {t('ui.action.cancel')}
            </SharedButton>

            <SharedButton
              dataTest="execute"
              onClick={() => {
                if (targetRole) {
                  onClickUpdateRole(admin.id, targetRole);
                }
                close();
              }}
              fontSize="small"
            >
              {t('ui.action.change')}
            </SharedButton>
          </>
        }
      >
        {t('ui.confirm.assignRole.body', {
          name: displayName,
          honorific: adminFullName ? t('ui.honorific') : ' ',
          roleType: targetRole
            ? t(`models.affiliation.roleType.${targetRole}.name`)
            : t('models.affiliation.roleType.member.name'),
        })}
      </Modal>
    </>
  );
};
